import type { UIMessage } from "ai";
import { cva, type VariantProps } from "class-variance-authority";
import type { ComponentProps, HTMLAttributes } from "react";

import { Avatar, AvatarFallback, AvatarImage } from "@src/components/ui/avatar";
import { cn } from "@src/lib/utils";

export type MessageProps = HTMLAttributes<HTMLDivElement> & {
  from: UIMessage["role"];
};

/**
 * Vertical rhythm of the message list (Figma "chat-panel" 15781:10901): each
 * message carries half of the gap it shares with its neighbour, so two Atlas
 * replies in a row sit 12px apart (6 + 6) and a user message is 20px from the
 * reply beside it (14 + 6). The work log carries the same 6px, so it sits 12px
 * above the reply it precedes.
 */
export const Message = ({ className, from, ...props }: MessageProps) => (
  <div
    className={cn(
      "group flex w-full min-w-0 max-w-full items-end justify-end gap-2",
      from === "user"
        ? "is-user py-3.5"
        : "is-assistant flex-row-reverse justify-end py-1.5",
      className
    )}
    {...props}
  />
);

const messageContentVariants = cva(
  "is-user:dark flex min-w-0 flex-col gap-2 rounded-lg text-sm",
  {
    variants: {
      variant: {
        contained: [
          "text-[12px]",
          "bg-secondary/50  dark:bg-secondary/30",
          "max-w-[80%] px-4 py-3",
          "group-[.is-user]:bg-primary group-[.is-user]:text-primary-foreground",
          "group-[.is-assistant]:bg-secondary group-[.is-assistant]:text-foreground",
        ],
        flat: [
          "text-[12px]",
          "group-[.is-user]:max-w-[80%] group-[.is-user]:bg-secondary group-[.is-user]:px-4 group-[.is-user]:py-3 group-[.is-user]:text-foreground",
          "group-[.is-assistant]:text-foreground",
        ],
      },
    },
    defaultVariants: {
      variant: "contained",
    },
  }
);

export type MessageContentProps = HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof messageContentVariants>;

export const MessageContent = ({
  children,
  className,
  variant,
  ...props
}: MessageContentProps) => (
  <div
    className={cn(messageContentVariants({ variant, className }))}
    {...props}
  >
    {/* Inner wrapper clips oversized children (wide images, long code, etc.)
        at the bubble's content-box edge so the variant's padding is
        preserved on all sides. Doing this on the bubble itself would clip
        at the padding-box edge, collapsing the right/bottom padding.
        `overflow-wrap: anywhere` breaks long unbreakable strings (URLs,
        hashes) so they wrap inside the bubble instead of forcing it
        wider than its `max-w` constraint. */}
    <div className="min-w-0 overflow-hidden [overflow-wrap:anywhere]">
      {children}
    </div>
  </div>
);

export type MessageAvatarProps = ComponentProps<typeof Avatar> & {
  src: string;
  name?: string;
};

export const MessageAvatar = ({
  src,
  name,
  className,
  ...props
}: MessageAvatarProps) => (
  <Avatar className={cn("size-8 ring-1 ring-border", className)} {...props}>
    <AvatarImage alt="" className="mt-0 mb-0" src={src} />
    <AvatarFallback>{name?.slice(0, 2) || "ME"}</AvatarFallback>
  </Avatar>
);
